import { FC, useState } from "react";
import { Accordion, Badge, Button, Text } from "@postenbring/hedwig-react";
import { AuditLogRepresentation } from "../infrastructure/api/api";
import { useSelectedOrganization } from "../infrastructure/organization/OrganizationContext";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { TranslationKey } from "../infrastructure/translation/translator";
import Translated from "../infrastructure/translation/Translated";
import { formatTimestamp } from "../Certificate/formatTimestamp";
import { AUDIT_LOG_PAGE_SIZE, auditLogApi } from "./auditLogApi";
import { useInitialAuditLog } from "./auditLogRouteContext";
import styles from "./AuditLogPage.module.css";

const AuditLogEntry: FC<{ entry: AuditLogRepresentation; index: number }> = ({
  entry,
  index,
}) => {
  const translate = useTranslate();
  const detailEntries = Object.entries(entry.details);

  const formatDetailValue = (key: string, value: string): string =>
    key === "reason"
      ? translate(`certificate.revoke_reason_${value}` as TranslationKey)
      : value;

  return (
    <Accordion.Item defaultOpen={false}>
      <Accordion.Header>
        <span className={styles.entryHeader}>
          <span className={styles.timestamp}>
            {formatTimestamp(entry.timestamp)}
          </span>
          <Badge data-color="neutral" size="smaller">
            {translate(`auditlog.type.${entry.type}` as TranslationKey)}
          </Badge>
          <span className={styles.actor}>{entry.actor}</span>
        </span>
      </Accordion.Header>
      <Accordion.Content>
        {entry.system && (
          <p className={styles.system}>
            <Translated
              textKey={"auditlog.system"}
              params={{ system: entry.system }}
            />
          </p>
        )}
        {detailEntries.length === 0 ? (
          <p className={styles.noDetails}>
            <Translated textKey={"auditlog.no_details"} />
          </p>
        ) : (
          <table className={styles.detailsTable}>
            <tbody>
              {detailEntries.map(([key, value]) => (
                <tr key={`${index}-${key}`}>
                  <th scope="row">
                    {translate(`auditlog.detail.${key}` as TranslationKey)}
                  </th>
                  <td>{formatDetailValue(key, value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Accordion.Content>
    </Accordion.Item>
  );
};

export const AuditLogPage: FC = () => {
  const organization = useSelectedOrganization();

  const initialEntries = useInitialAuditLog();
  const [entries, setEntries] =
    useState<AuditLogRepresentation[]>(initialEntries);
  const [nextPage, setNextPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(
    initialEntries.length === AUDIT_LOG_PAGE_SIZE,
  );

  const loadMore = async () => {
    setLoading(true);
    try {
      const page = await auditLogApi(organization).getPage(nextPage);
      setEntries((current) => [...current, ...page]);
      setNextPage((current) => current + 1);
      setHasMore(page.length === AUDIT_LOG_PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <Text variant="h1" as="h1" size="min">
        <Translated textKey={"auditlog.header"} />
      </Text>

      {entries.length === 0 ? (
        <p className={styles.emptyState}>
          <Translated textKey={"auditlog.empty"} />
        </p>
      ) : (
        <Accordion>
          {entries.map((entry, index) => (
            <AuditLogEntry
              key={`${entry.timestamp}-${index}`}
              entry={entry}
              index={index}
            />
          ))}
        </Accordion>
      )}

      {hasMore && (
        <Button variant="secondary" onClick={loadMore} disabled={loading}>
          <Translated
            textKey={loading ? "auditlog.loading" : "auditlog.load_more"}
          />
        </Button>
      )}
    </div>
  );
};
